/**
 * NetworkStatusBanner
 * ─────────────────────────────────────────────────────────────────────────────
 * Google Meet–style network status banner.
 *
 * Positions:
 *   Desktop/tablet  → top-right  (never collides with meeting timer top-left)
 *   Mobile portrait → bottom-center above the control bar
 *   Landscape mobile → top-right compact
 *
 * States:
 *   connected    → hidden
 *   reconnecting → dark charcoal + spinner  "Trying to reconnect…"
 *   success      → green "Reconnected ✓"  (3 s then hides)
 *   failed       → red   "Connection lost. Call ended."
 *
 * Video freeze:
 *   Adds / removes .network-frozen on #videos_portal so CSS ::before/::after
 *   pseudo-elements overlay each tile with a blur — exactly the Google Meet
 *   "video paused" behaviour. The stream is NOT stopped; it resumes on reconnect.
 *
 * Props: none — reads directly from Redux store via connect().
 */

import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { NETWORK_STATUS } from "../../utils/networkConnectionManager";
import "./NetworkStatusBanner.css";

/* ── Helper: toggle .network-frozen on the videos portal ── */
const setVideosFrozen = (frozen) => {
  const portal = document.getElementById("videos_portal");
  if (!portal) return;
  if (frozen) {
    portal.classList.add("network-frozen");
  } else {
    portal.classList.remove("network-frozen");
  }
};

/* ─────────────────────────────────────────────────────────────────────────── */

const NetworkStatusBanner = ({ networkStatus }) => {
  const [justReconnected, setJustReconnected] = useState(false);
  const prevStatusRef = useRef(networkStatus);
  const successTimerRef = useRef(null);

  /* ── Detect transition: reconnecting → connected ── */
  useEffect(() => {
    const prev = prevStatusRef.current;
    const curr = networkStatus;

    if (prev === NETWORK_STATUS.RECONNECTING && curr === NETWORK_STATUS.CONNECTED) {
      // Came back online — show green success banner briefly
      setJustReconnected(true);
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
      successTimerRef.current = setTimeout(() => {
        setJustReconnected(false);
      }, 3000);
    }

    prevStatusRef.current = curr;
  }, [networkStatus]);

  /* ── Freeze / unfreeze video tiles ── */
  useEffect(() => {
    const shouldFreeze =
      networkStatus === NETWORK_STATUS.RECONNECTING ||
      networkStatus === NETWORK_STATUS.FAILED;
    setVideosFrozen(shouldFreeze);
  }, [networkStatus]);

  /* ── Cleanup ── */
  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
      // Ensure freeze is removed if component unmounts (room left)
      setVideosFrozen(false);
    };
  }, []);

  /* ── Derive render state ── */
  const isReconnecting = networkStatus === NETWORK_STATUS.RECONNECTING;
  const isFailed       = networkStatus === NETWORK_STATUS.FAILED;
  const isVisible      = isReconnecting || isFailed || justReconnected;

  if (!isVisible) return null;

  let bannerClass = "network-banner";
  let message     = "";
  let showSpinner = false;

  if (isFailed) {
    bannerClass += " network-banner--failed";
    message      = "Connection lost. Call ended.";
  } else if (justReconnected) {
    bannerClass += " network-banner--success";
    message      = "Reconnected";
  } else {
    bannerClass += " network-banner--reconnecting";
    message      = "Trying to reconnect…";
    showSpinner  = true;
  }

  return (
    <div className={bannerClass} role="alert" aria-live="assertive">
      {showSpinner && (
        <span className="network-banner__spinner" aria-hidden="true" />
      )}
      {!showSpinner && !isFailed && (
        <span className="network-banner__icon" aria-hidden="true">✓</span>
      )}
      {isFailed && (
        <span
          className="network-banner__icon network-banner__icon--failed"
          aria-hidden="true"
        >
          ⚠
        </span>
      )}
      <span className="network-banner__text">{message}</span>
    </div>
  );
};

const mapStoreStateToProps = (state) => ({
  networkStatus: state.networkStatus,
});

export default connect(mapStoreStateToProps)(NetworkStatusBanner);